import { useState, useEffect } from 'react';
import { X, Save, Mail, Palette, CheckCircle, AlertCircle, Loader2, Send, Database, Plus, Trash2, RefreshCw } from 'lucide-react';
import { API_URL } from '../config';

interface EmailSettings {
  provider: string;
  from_email: string;
  from_name: string;
  sendgrid_api_key: string;
  smtp_host: string;
  smtp_port: number;
  smtp_user: string;
  smtp_password: string;
  use_tls: boolean;
}

interface BrandingSettings {
  company_name: string;
  logo_url: string;
  primary_color: string;
}

interface WorkspaceConfig {
  name: string;
  email_settings: EmailSettings;
  branding: BrandingSettings;
}

interface WorkspaceSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId?: string;
}

export default function WorkspaceSettings({ isOpen, onClose, workspaceId = "default" }: WorkspaceSettingsProps) {
  const [config, setConfig] = useState<WorkspaceConfig>({
    name: "Oystar Studio",
    email_settings: {
      provider: "sendgrid",
      from_email: "",
      from_name: "Oystar Studio",
      sendgrid_api_key: "",
      smtp_host: "",
      smtp_port: 587,
      smtp_user: "",
      smtp_password: "",
      use_tls: true
    },
    branding: {
      company_name: "Oystar Studio",
      logo_url: "",
      primary_color: "#6366f1"
    }
  });

  const [activeTab, setActiveTab] = useState<'email' | 'branding' | 'kb'>('email');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // KB Aliases state
  const [aliases, setAliases] = useState<Record<string, string>>({});
  const [availableIndices, setAvailableIndices] = useState<{name: string, chunks_count: number}[]>([]);
  const [newAlias, setNewAlias] = useState('');
  const [newIndexName, setNewIndexName] = useState('');
  const [loadingAliases, setLoadingAliases] = useState(false);

  // Carica config all'apertura
  useEffect(() => {
    if (isOpen) {
      loadConfig();
      loadAliases();
      loadIndices();
    }
  }, [isOpen, workspaceId]);

  // Funzioni per KB Aliases
  const loadAliases = async () => {
    setLoadingAliases(true);
    try {
      const response = await fetch(`${API_URL}/api/kb-aliases/`);
      if (response.ok) {
        const data = await response.json();
        setAliases(data.aliases || {});
      }
    } catch (error) {
      console.error('Errore caricamento aliases:', error);
    } finally {
      setLoadingAliases(false);
    }
  };

  const loadIndices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/flow/kb/list-indices`);
      if (response.ok) {
        const data = await response.json();
        const indices = data.indices || [];
        setAvailableIndices(indices.map((idx: any) => ({
          name: typeof idx === 'string' ? idx : idx.name,
          chunks_count: idx.chunks_count || 0
        })));
      }
    } catch (error) {
      console.error('Errore caricamento indici:', error);
    }
  };

  const addAlias = async () => {
    if (!newAlias.trim() || !newIndexName.trim()) {
      setMessage({ type: 'error', text: 'Inserisci alias e nome indice' });
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/kb-aliases/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ alias: newAlias, index_name: newIndexName })
      });
      if (response.ok) {
        await loadAliases();
        setNewAlias('');
        setNewIndexName('');
        setMessage({ type: 'success', text: `✅ Alias "${newAlias}" creato!` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: '❌ Errore creazione alias' });
    }
  };

  const deleteAlias = async (alias: string) => {
    if (!confirm(`Eliminare l'alias "${alias}"?`)) return;
    try {
      const response = await fetch(`${API_URL}/api/kb-aliases/${encodeURIComponent(alias)}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        await loadAliases();
        setMessage({ type: 'success', text: `✅ Alias "${alias}" eliminato` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: '❌ Errore eliminazione alias' });
    }
  };

  const syncAliases = async () => {
    setLoadingAliases(true);
    try {
      const response = await fetch(`${API_URL}/api/kb-aliases/sync-from-kb`, {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        await loadAliases();
        setMessage({ 
          type: 'success', 
          text: `✅ Sincronizzati ${data.aliases_added?.length || 0} nuovi alias` 
        });
      }
    } catch (error) {
      setMessage({ type: 'error', text: '❌ Errore sincronizzazione' });
    } finally {
      setLoadingAliases(false);
    }
  };

  const loadConfig = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const response = await fetch(`${API_URL}/workspace/config?workspace_id=${workspaceId}`);
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Errore caricamento config:', error);
      setMessage({ type: 'error', text: 'Errore caricamento configurazione' });
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const response = await fetch(`${API_URL}/workspace/config?workspace_id=${workspaceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      if (response.ok) {
        setMessage({ type: 'success', text: '✅ Configurazione salvata!' });
      } else {
        throw new Error('Errore salvataggio');
      }
    } catch (error) {
      setMessage({ type: 'error', text: '❌ Errore salvataggio configurazione' });
    } finally {
      setSaving(false);
    }
  };

  const testEmailConnection = async () => {
    setTesting(true);
    setMessage(null);
    try {
      // Prima salva, poi testa
      await saveConfig();
      
      const response = await fetch(`${API_URL}/workspace/test-email?workspace_id=${workspaceId}`, {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.status === 'success') {
        setMessage({ type: 'success', text: `✅ ${data.message}` });
      } else {
        setMessage({ type: 'error', text: `❌ ${data.message}` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: '❌ Errore test connessione email' });
    } finally {
      setTesting(false);
    }
  };

  const updateEmailSettings = (field: keyof EmailSettings, value: string | number | boolean) => {
    setConfig(prev => ({
      ...prev,
      email_settings: {
        ...prev.email_settings,
        [field]: value
      }
    }));
  };

  const updateBranding = (field: keyof BrandingSettings, value: string) => {
    setConfig(prev => ({
      ...prev,
      branding: {
        ...prev.branding,
        [field]: value
      }
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              ⚙️
            </div>
            <div>
              <h2 className="text-xl font-bold">Workspace Settings</h2>
              <p className="text-purple-200 text-sm">Configura email, branding e preferenze</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('email')}
            className={`flex-1 px-4 py-3 flex items-center justify-center gap-2 font-medium transition
              ${activeTab === 'email' 
                ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50' 
                : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            <Mail className="w-4 h-4" />
            Email Settings
          </button>
          <button
            onClick={() => setActiveTab('branding')}
            className={`flex-1 px-4 py-3 flex items-center justify-center gap-2 font-medium transition
              ${activeTab === 'branding' 
                ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50' 
                : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            <Palette className="w-4 h-4" />
            Branding
          </button>
          <button
            onClick={() => setActiveTab('kb')}
            className={`flex-1 px-4 py-3 flex items-center justify-center gap-2 font-medium transition
              ${activeTab === 'kb' 
                ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50' 
                : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            <Database className="w-4 h-4" />
            KB Aliases
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
            </div>
          ) : activeTab === 'email' ? (
            <div className="space-y-5">
              {/* Provider */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  📧 Provider Email
                </label>
                <select
                  value={config.email_settings.provider}
                  onChange={e => updateEmailSettings('provider', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="sendgrid">SendGrid (Consigliato)</option>
                  <option value="smtp">SMTP Custom</option>
                  <option value="mailgun">Mailgun</option>
                </select>
              </div>

              {/* From Email + Name */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    📮 Email Mittente
                  </label>
                  <input
                    type="email"
                    value={config.email_settings.from_email}
                    onChange={e => updateEmailSettings('from_email', e.target.value)}
                    placeholder="info@tuodominio.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    👤 Nome Mittente
                  </label>
                  <input
                    type="text"
                    value={config.email_settings.from_name}
                    onChange={e => updateEmailSettings('from_name', e.target.value)}
                    placeholder="Oystar Studio - Cliente X"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Es: "Oystar Studio - Cliente X" apparirà come mittente
                  </p>
                </div>
              </div>

              {/* SendGrid API Key */}
              {config.email_settings.provider === 'sendgrid' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    🔑 SendGrid API Key
                  </label>
                  <input
                    type="password"
                    value={config.email_settings.sendgrid_api_key}
                    onChange={e => updateEmailSettings('sendgrid_api_key', e.target.value)}
                    placeholder="SG.xxxxxx..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Se vuoto, usa la chiave dalle variabili ambiente del server
                  </p>
                </div>
              )}

              {/* SMTP Settings */}
              {config.email_settings.provider === 'smtp' && (
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        🖥️ SMTP Host
                      </label>
                      <input
                        type="text"
                        value={config.email_settings.smtp_host}
                        onChange={e => updateEmailSettings('smtp_host', e.target.value)}
                        placeholder="smtp.gmail.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        🚪 Porta
                      </label>
                      <input
                        type="number"
                        value={config.email_settings.smtp_port}
                        onChange={e => updateEmailSettings('smtp_port', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        👤 Username
                      </label>
                      <input
                        type="text"
                        value={config.email_settings.smtp_user}
                        onChange={e => updateEmailSettings('smtp_user', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        🔒 Password
                      </label>
                      <input
                        type="password"
                        value={config.email_settings.smtp_password}
                        onChange={e => updateEmailSettings('smtp_password', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="use_tls"
                      checked={config.email_settings.use_tls}
                      onChange={e => updateEmailSettings('use_tls', e.target.checked)}
                      className="w-4 h-4 text-purple-600"
                    />
                    <label htmlFor="use_tls" className="text-sm text-gray-700">
                      Usa TLS/SSL
                    </label>
                  </div>
                </div>
              )}

              {/* Test Email Button */}
              <button
                onClick={testEmailConnection}
                disabled={testing}
                className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition"
              >
                {testing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Test Connessione Email
              </button>
            </div>
          ) : activeTab === 'branding' ? (
            <div className="space-y-5">
              {/* Workspace Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  🏢 Nome Workspace
                </label>
                <input
                  type="text"
                  value={config.name}
                  onChange={e => setConfig(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Oystar Studio"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Company Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  🏷️ Nome Azienda (per email)
                </label>
                <input
                  type="text"
                  value={config.branding.company_name}
                  onChange={e => updateBranding('company_name', e.target.value)}
                  placeholder="La Tua Azienda Srl"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Logo URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  🖼️ URL Logo
                </label>
                <input
                  type="url"
                  value={config.branding.logo_url}
                  onChange={e => updateBranding('logo_url', e.target.value)}
                  placeholder="https://tuosito.com/logo.png"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Primary Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  🎨 Colore Primario
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={config.branding.primary_color}
                    onChange={e => updateBranding('primary_color', e.target.value)}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={config.branding.primary_color}
                    onChange={e => updateBranding('primary_color', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                  />
                </div>
              </div>

              {/* Preview */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-3">Anteprima Email</p>
                <div className="bg-white border rounded-lg p-4 text-center">
                  {config.branding.logo_url ? (
                    <img 
                      src={config.branding.logo_url} 
                      alt="Logo" 
                      className="h-10 mx-auto mb-2"
                      onError={(e) => (e.target as HTMLImageElement).style.display = 'none'}
                    />
                  ) : (
                    <div 
                      className="w-12 h-12 mx-auto mb-2 rounded-lg flex items-center justify-center text-white font-bold text-xl"
                      style={{ backgroundColor: config.branding.primary_color }}
                    >
                      {config.branding.company_name.charAt(0)}
                    </div>
                  )}
                  <p className="font-semibold" style={{ color: config.branding.primary_color }}>
                    {config.branding.company_name}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Da: {config.email_settings.from_name || config.branding.company_name}
                    {config.email_settings.from_email && ` <${config.email_settings.from_email}>`}
                  </p>
                </div>
              </div>
            </div>
          ) : activeTab === 'kb' ? (
            /* KB Aliases Tab */
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-800">🏷️ Alias Knowledge Base</h3>
                  <p className="text-sm text-gray-500">
                    Usa nomi semplici invece dei nomi tecnici degli indici
                  </p>
                </div>
                <button
                  onClick={syncAliases}
                  disabled={loadingAliases}
                  className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg flex items-center gap-1"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingAliases ? 'animate-spin' : ''}`} />
                  Sync Auto
                </button>
              </div>

              {/* Add new alias */}
              <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                <p className="text-sm font-medium text-gray-700">➕ Aggiungi nuovo alias</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newAlias}
                    onChange={e => setNewAlias(e.target.value)}
                    placeholder="Nome semplice (es. ANCONA)"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <select
                    value={newIndexName}
                    onChange={e => setNewIndexName(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  >
                    <option value="">Seleziona indice...</option>
                    {availableIndices.map(idx => (
                      <option key={idx.name} value={idx.name}>
                        {idx.name.length > 30 ? idx.name.substring(0, 30) + '...' : idx.name} ({idx.chunks_count.toLocaleString()} chunks)
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={addAlias}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Aliases list */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">
                  📚 Alias configurati ({Object.keys(aliases).length})
                </p>
                {loadingAliases ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
                  </div>
                ) : Object.keys(aliases).length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    Nessun alias configurato. Usa "Sync Auto" per crearli automaticamente.
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
                    {Object.entries(aliases).map(([alias, indexName]) => (
                      <div 
                        key={alias}
                        className="flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="font-medium text-purple-700 bg-purple-100 px-2 py-1 rounded text-sm">
                            {alias}
                          </span>
                          <span className="text-gray-400">→</span>
                          <span className="text-sm text-gray-600 truncate" title={indexName}>
                            {indexName.length > 35 ? indexName.substring(0, 35) + '...' : indexName}
                          </span>
                        </div>
                        <button
                          onClick={() => deleteAlias(alias)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                          title="Elimina alias"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Help text */}
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                <p className="font-medium mb-1">💡 Come usare gli alias</p>
                <p>
                  Nel nodo <strong>RAG Simple</strong>, scrivi semplicemente <code className="bg-blue-100 px-1 rounded">ANCONA</code> 
                  invece del nome completo dell'indice. AI Help li riconosce automaticamente!
                </p>
              </div>
            </div>
          ) : null}

          {/* Message */}
          {message && (
            <div className={`mt-4 p-3 rounded-lg flex items-center gap-2 ${
              message.type === 'success' 
                ? 'bg-green-50 text-green-700 border border-green-200' 
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {message.type === 'success' ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <AlertCircle className="w-5 h-5" />
              )}
              <span className="font-medium">{message.text}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition"
          >
            Annulla
          </button>
          <button
            onClick={saveConfig}
            disabled={saving}
            className="px-6 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white rounded-lg font-medium flex items-center gap-2 transition"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Salva
          </button>
        </div>
      </div>
    </div>
  );
}
